// Tribe palette. Fields:
//  bg          — page background
//  surface     — card background on top of bg
//  panel       — the tribe-flavored "hero" panel background (Start-Here, cert callouts)
//  panel_soft  — softer nested panel background inside the hero panel
//  panel_ink   — legible text color placed on top of `panel`
//  accent      — the tribe's identifiable primary color (used for headers, borders, text labels)
//  ink         — main text color on light surfaces
//  subtle      — smaller highlight color (tribe secondary — yellow for Nasal Ninja, sky blue for Myo Princess)
//  spark       — celebratory/sparse highlight (red for Nasal Ninja, gold for Myo Princess)
//  spark_ink   — legible text/icon color placed on top of `spark`
//  emblem_svg  — inline SVG data URL for the tribe badge (a compact identity marker)
//  emblem_bg   — solid color behind the emblem (matches its outer ring)
const emblem = (svgInner, size = 64) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">${svgInner}</svg>`
  )}`;

// Nasal Ninja emblem — SMALL-CONTEXT variant.
// A single 4-point shuriken (ninja star) on the deep blue disc. Chosen over
// the earlier masked-face design because one bold silhouette reads as
// "ninja" at any avatar size (32–64px) without competing details.
const NN_EMBLEM_SVG = `
  <defs>
    <linearGradient id="nnBg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#5A93C7"/>
      <stop offset="1" stop-color="#3E78AF"/>
    </linearGradient>
  </defs>
  <circle cx="32" cy="32" r="30" fill="url(#nnBg)" stroke="#FAB41F" stroke-width="3"/>
  <path d="M32 8 L37 27 L56 32 L37 37 L32 56 L27 37 L8 32 L27 27 Z" fill="#FAB41F" stroke="#1E2A3A" stroke-width="1.6" stroke-linejoin="round"/>
  <circle cx="32" cy="32" r="4" fill="#FF3131" stroke="#1E2A3A" stroke-width="1.4"/>
`;

// Myo Princess emblem — SMALL-CONTEXT variant.
// Bold gold crown centered on a pink disc; readable at 32–64px where the
// full illustrated badge (hair, banner, flowers, sparkles) becomes visual noise.
const MP_EMBLEM_SVG = `
  <defs>
    <linearGradient id="mpBg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#F5A6BE"/>
      <stop offset="1" stop-color="#F391AB"/>
    </linearGradient>
  </defs>
  <circle cx="32" cy="32" r="30" fill="url(#mpBg)" stroke="#E8D5B0" stroke-width="3"/>
  <path d="M12 42 L18 22 L26 34 L32 18 L38 34 L46 22 L52 42 Z" fill="#E8D5B0" stroke="#B99A5A" stroke-width="1.6" stroke-linejoin="round"/>
  <rect x="12" y="42" width="40" height="5" fill="#E8D5B0" stroke="#B99A5A" stroke-width="1.2"/>
  <circle cx="18" cy="22" r="3" fill="#9FDDFE" stroke="#FFFEF9" stroke-width="1"/>
  <circle cx="32" cy="17" r="3.6" fill="#9FDDFE" stroke="#FFFEF9" stroke-width="1"/>
  <circle cx="46" cy="22" r="3" fill="#9FDDFE" stroke="#FFFEF9" stroke-width="1"/>
`;



// Thrive Tribe emblem — neutral cream disc, gold ring, subtle mauve center.
const THRIVE_EMBLEM_SVG = `
  <circle cx="32" cy="32" r="30" fill="#FFFEF9" stroke="#C9A96E" stroke-width="3"/>
  <circle cx="32" cy="32" r="18" fill="none" stroke="#C9A96E" stroke-width="2"/>
  <path d="M32 18 L36 30 L48 30 L38 38 L42 50 L32 43 L22 50 L26 38 L16 30 L28 30 Z" fill="#C9A96E"/>
`;

// Thrive Story emblem — placeholder star on a soft cream disc. This emblem is
// a fallback: once an admin uploads the family's "adventure companion" photo,
// TribeEmblem swaps in that photo at the same spot/size.
const TS_EMBLEM_SVG = `
  <defs>
    <linearGradient id="tsBg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#FBF5FA"/>
      <stop offset="1" stop-color="#F4EBE4"/>
    </linearGradient>
  </defs>
  <circle cx="32" cy="32" r="30" fill="url(#tsBg)" stroke="#8B5E7A" stroke-width="3"/>
  <path d="M32 14 L37 28 L52 29 L40 38 L44 52 L32 44 L20 52 L24 38 L12 29 L27 28 Z" fill="#8B5E7A" stroke="#4A2C3A" stroke-width="1.4" stroke-linejoin="round"/>
`;

export const TRIBES = {
  neutral: {
    key: "neutral",
    name: "Your journey",
    welcome_text: "",
    manifesto: "",
    next_steps: [],
    graduation_name: "Program Complete",
    graduation_description: "twelve sessions finished — steady, thoughtful progress toward better breathing.",
    bg: "#FAF7F4",
    surface: "#FFFEF9",
    panel: "#FFFEF9",
    panel_soft: "#FAF7F4",
    panel_ink: "#1E1E2E",
    accent: "#C9A96E",
    ink: "#1E1E2E",
    subtle: "#4A4A6A",
    spark: "#C9A96E",
    spark_ink: "#1E1E2E",
    emblem_svg: emblem(THRIVE_EMBLEM_SVG),
    emblem_bg: "#FFFEF9",
  },
  thrive_tribe: {
    key: "thrive_tribe",
    name: "Thrive Tribe",
    welcome_text:
      "Welcome to the Thrive Tribe — a home for kids who want a bright, welcoming space with no theme layered on top. Here, you'll breathe with intention, lift your tongue, seal your lips, and settle your body into calm strength. Over the next 12 sessions you'll grow — not toward perfect, but toward peace.",
    manifesto:
      "I am the Thrive Tribe. I breathe with intention. My tongue lifts, my lips seal, my body settles into calm strength. Every day I grow — not toward perfect, but toward peace.",
    next_steps: [
      "Watch your next session video with your grownup.",
      "Use the tools from your Transformation Toolkit when the video tells you to.",
      "Take a picture with your certificate and send it in — that's how we track your donations and celebrate your progress.",
      "Wear your Thrive Tribe bracelet during training — you've earned it.",
    ],
    graduation_name: "Thrive Tribe Hall of Fame",
    graduation_description:
      "where the calm, consistent kids who chose intentional breathing are honored for their steady progress and quiet strength.",
    bg: "#FAF7F4",
    surface: "#FFFEF9",
    panel: "#FFFEF9",
    panel_soft: "#FAF7F4",
    panel_ink: "#1E1E2E",
    accent: "#C9A96E",
    ink: "#1E1E2E",
    subtle: "#4A4A6A",
    spark: "#E8D5B0",
    spark_ink: "#1E1E2E",
    emblem_svg: emblem(THRIVE_EMBLEM_SVG),
    emblem_bg: "#FFFEF9",
    // Path to the illustrated manifesto — PDF for download/print, PNG for
    // reliable inline rendering. Admin CMS can override via
    // `tribe.<key>.manifesto_pdf_url` / `manifesto_image_url`.
    manifesto_pdf: "/manifestos/thrive_tribe.pdf",
    manifesto_image: "/manifestos/thrive_tribe.png",
  },
  nasal_ninja: {
    key: "nasal_ninja",
    name: "Nasal Ninja",
    welcome_text:
      "Welcome, Nasal Ninja in training. You've just entered the Nasal Ninja Tribe, where kids like you learn to breathe strong, sleep deep, and thrive every single day. You are not just here to stretch your tongue or blow through some straws — you are here to unlock your breathing superpowers, because the real ninjas know that the most powerful strength is hidden inside the breath.\n\nOver the next 12 missions — also called your therapy sessions — you will train your muscles and your breath, build silent, powerful sleep habits, earn certificates and rewards, and help other kids around the world through your effort. Every session you complete, $1 is donated to children in need. By the time you become a full Nasal Ninja, you'll have helped 12 other kids breathe better too.",
    manifesto:
      "I am a Nasal Ninja. I breathe through my nose because that's what makes me strong. I keep my lips together because that's how true ninjas breathe. I train my tongue and muscles so my airway stays open and powerful. I do not let mouth breathing slow me down. I do not let bad habits sneak up on me. I know that breathing right makes me faster, smarter, and stronger. At night, I sleep like a true ninja — silent, still, breathing deep. I let my body rest, grow, and recharge for my next adventure. I am not just any kid. I am a Nasal Ninja, and I will breathe my way to victory.",
    next_steps: [
      "Watch your first session video with your grownup.",
      "Use the tools from your Transformation Toolkit when the video tells you to.",
      "After each mission, take a photo with your certificate and send it in — that's how we'll track your donations.",
      "Wear your Nasal Ninja tribe bracelet during training — it's your badge of strength and you've absolutely earned it.",
    ],
    graduation_name: "Nasal Ninja Hall of Fame",
    graduation_description:
      "a place where strong breathers are honored for their power, patience, and progress.",
    // Palette — pulled from real Nasal Ninja badge artwork.
    bg: "#EAF2FA",         // soft blue-white page bg
    surface: "#FFFFFF",    // white cards on top of bg
    panel: "#3E78AF",      // deep blue "hero" panel bg (Start Here, cert callouts)
    panel_soft: "#5A93C7", // lighter blue for nested panels inside a panel
    panel_ink: "#FFFEF9",  // white text on the blue panel
    accent: "#3E78AF",     // primary blue — headers, borders, text labels
    ink: "#1E2A3A",        // dark navy text on light surfaces
    subtle: "#FAB41F",     // yellow highlight (badges, small accents, celebrations)
    spark: "#FF3131",      // sparing red — reserved for celebratory moments only
    spark_ink: "#FFFEF9",   // white text on the red spark background
    // Small-context icon: bold shuriken silhouette that stays legible at
    // 32–64px (family dashboard cards, child selectors, session headers).
    emblem_svg: emblem(NN_EMBLEM_SVG),
    emblem_bg: "#3E78AF",
    // Large-context artwork slot — same pattern as Myo Princess. Drop the
    // detailed illustrated Nasal Ninja badge PNG at the path below and it
    // will auto-render at ≥80px (certificate, hero-sized headers). Until
    // the artwork is uploaded, TribeEmblem stays on the shuriken icon.
    // emblem_svg_large: "/tribes/nasal_ninja_badge.png",
    // emblem_bg_large: "transparent",
    // Illustrated manifesto pair — PDF drives Download + Print, the PNG is the
    // reliable inline visual for every browser.
    manifesto_pdf: "/manifestos/nasal_ninja.pdf",
    manifesto_image: "/manifestos/nasal_ninja.png",
  },
  myo_princess: {
    key: "myo_princess",
    name: "Myo Princess",
    academy_name: "Royal Myo Academy",
    welcome_text:
      "Hello, beautiful Myo Princess in training and welcome to the Royal Myo Academy. Brave, graceful girls just like you begin a journey of strength, confidence, and transformation. You're not just here to do exercises. You're here to step into your royal power. You are about to become someone who breathes through their nose, sleeps like a queen, and grows into the strongest version of herself inside and out.\n\nOver the next 12 sessions, you'll complete royal therapy missions, earn your princess ranks, donate $1 per session to help kids around the world, collect certificates, letters, and surprises, and finally become an official Myo Queen.",
    manifesto:
      "I am a Myo Princess. I stand tall, I breathe strong, and I grow with confidence. I know that true princesses breathe through their noses, not their mouths. I keep my tongue in the right place because that's what builds my royal strength. I do not let bad habits weaken me. I do not let mouth breathing take away my energy. I know that proper breathing makes me strong, focused, and ready to rule my world. At night, I sleep like a true princess — calm, silent, breathing through my nose. I let my body rest, heal, and grow stronger every day. I am not just any girl. I am a Myo Princess, and I will breathe, sleep, and grow like royalty.",
    next_steps: [
      "Watch your next session video with your grownup.",
      "Use the right tools from your Transformation Toolkit.",
      "Take a picture with your certificate and send it in — that's how we track your donations and celebrate your progress.",
      "Wear your Myo Princess Thrive Tribe bracelet — you've earned it.",
    ],
    graduation_name: "Myo Queen",
    graduation_description:
      "where the strongest, kindest, and most powerful princesses in the world are celebrated.",
    // Palette — pink dominant, lavender surface, sky-blue accent, gold-light decorative.
    bg: "#FBF5FA",         // soft lavender-pink page bg
    surface: "#FFFFFF",    // white cards
    panel: "#F391AB",      // pink hero panel bg
    panel_soft: "#E7D8F7", // lavender for nested panels inside the pink panel
    panel_ink: "#FFFEF9",  // white text on pink panel
    accent: "#F391AB",     // primary pink — headers, borders, CTAs
    ink: "#4A2C3A",        // deep plum text on light surfaces
    subtle: "#9FDDFE",     // sky-blue highlight (smaller accents, tribe secondary)
    spark: "#E8D5B0",      // gold-light for ornate/celebratory flourishes
    spark_ink: "#4A2C3A",   // deep plum text on the gold-light spark background
    // Small-context icon: a single, bold crown symbol that stays legible at
    // 32–64px (family dashboard cards, child selectors, session headers).
    emblem_svg: emblem(MP_EMBLEM_SVG),
    emblem_bg: "#F391AB",
    // Large-context artwork: the full illustrated Myo Princess badge, reserved
    // for ≥80px displays like the certificate and hero-sized headers.
    emblem_svg_large: "/tribes/myo_princess_badge.png",
    emblem_bg_large: "transparent",
    // Illustrated manifesto PDF shown on the post-tribe-choice induction screen.
    manifesto_pdf: "/manifestos/myo_princess.pdf",
    // Pre-rendered PNG of the manifesto's first page — used as the inline visual
    // (browsers refuse to render PDFs inside iframes without a PDF viewer plugin,
    // and mobile Safari force-downloads them). The PDF above stays the source of
    // truth for the Download + Print buttons.
    manifesto_image: "/manifestos/myo_princess.png",
  },
  // Bonus-gated 5th tribe. The actual tribe experience is personalized per-family —
  // favorite color becomes the accent/spark, and the uploaded "adventure companion"
  // photo becomes the circular emblem. The static palette below is only the
  // "pending" / fallback state before the admin publishes the family's story.
  thrive_story: {
    key: "thrive_story",
    name: "My Thrive Story",
    welcome_text:
      "Welcome to your very own Thrive Story — a one-of-a-kind journey built just for you, with your adventure companion by your side. While we create your personalized world, you can still practice, learn, and watch for the moment your story comes alive.",
    manifesto:
      "This is my Thrive Story. My breath is my power, my companion is my courage. I grow stronger every day, in my own way.",
    next_steps: [
      "Watch your next session video with your grownup.",
      "Use the tools from your Transformation Toolkit when the video tells you to.",
      "Collect your personalized certificates after each session — they're drawn just for you.",
    ],
    graduation_name: "My Thrive Story Hall of Legends",
    graduation_description:
      "where every custom journey ends in a celebration crafted around the hero's own adventure.",
    bg: "#FBF5FA",
    surface: "#FFFEF9",
    panel: "#FFFEF9",
    panel_soft: "#FBF5FA",
    panel_ink: "#1E1E2E",
    accent: "#8B5E7A",
    ink: "#1E1E2E",
    subtle: "#4A4A6A",
    spark: "#C9A96E",
    spark_ink: "#1E1E2E",
    emblem_svg: emblem(TS_EMBLEM_SVG),
    emblem_bg: "#FBF5FA",
  },
};

/**
 * Returns a tribe palette, but — if the child is on the Thrive Story tribe AND
 * has published assets (favorite color + companion photo) — layers the family's
 * favorite color on top of the accent + spark channels. All other fields stay
 * on the neutral/calm base so text contrast is never broken by an unknown hex.
 *
 * Use this instead of `TRIBES[child.tribe]` in any screen that needs the
 * personalized Thrive Story accent (ChildHome, SessionDetail, Certificate).
 */
export function tribeForChild(child) {
  const base = TRIBES[child?.tribe] || TRIBES.neutral;
  if (child?.tribe !== "thrive_story" || !child?.story_assets_ready) return base;
  const fc = child?.favorite_color;
  if (!fc || !/^#[0-9A-Fa-f]{6}$/.test(fc)) return base;
  return { ...base, accent: fc, spark: fc };
}

export const SYMPTOM_OPTIONS = [
  { key: "sleep_apnea", name: "Signs of Sleep Apnea (gasping, choking, or pauses in breathing at night)" },
  { key: "snoring", name: "Snoring" },
  { key: "mouth_breathing", name: "Mouth Breathing" },
  { key: "restless_sleep", name: "Restless Sleep" },
  { key: "bedwetting", name: "Bedwetting" },
  { key: "dark_circles", name: "Dark Circles Under Eyes" },
  { key: "crooked_teeth_narrow_palate", name: "Crooked/Crowded Teeth/Narrow Palate" },
  { key: "thumb_sucking", name: "Thumb Sucking / Pacifier" },
  { key: "tongue_tie", name: "Tongue Tie" },
  { key: "enlarged_tonsils", name: "Enlarged Tonsils / Adenoids" },
  { key: "ear_infections", name: "Chronic Ear Infections" },
  { key: "allergies", name: "Allergies / Congestion" },
  { key: "speech_delays", name: "Speech Delays" },
  { key: "focus_issues", name: "Behavior/Focus/Hyperactivity" },
  { key: "digestive_issues", name: "Digestive Issues" },
  { key: "frequent_cavities", name: "Frequent Cavities" },
  { key: "asthma", name: "Asthma" },
];
